import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchStudents } from "../../store/studentsSlice";
import {
   notifyDocumentReady,
   testNotificationsTomorrow,
} from "../../api/notificationsService";

// Mesaje de test care imită situațiile reale din platformă.
const PRESET_MESSAGES = [
   "Documentele tale au fost procesate și sunt gata de ridicare de la secretariat.",
   "Rezultatul examenului practic a fost înregistrat. Verifică statusul documentelor.",
   "Instructorul ți-a recomandat un videoclip pentru lucru individual.",
   "Te rugăm să programezi următoarele 15 lecții.",
];

function asRole(user) {
   return String(user?.role ?? user?.userRole ?? "")
      .trim()
      .toUpperCase();
}

function isStudentUser(user) {
   const role = asRole(user);
   return role === "USER" || role === "STUDENT";
}

function studentLabel(u) {
   const name = `${u?.firstName || ""} ${u?.lastName || ""}`.trim();
   return name ? `${name} (#${u.id})` : `Elev #${u.id}`;
}

export default function NotificationsSeeder() {
   const dispatch = useDispatch();
   const studentsRaw = useSelector((s) => s.students?.list || []);
   const studentsLoading = useSelector((s) => s.students?.loading);

   const [userId, setUserId] = useState("");
   const [message, setMessage] = useState(PRESET_MESSAGES[0]);
   const [count, setCount] = useState(1);
   const [busy, setBusy] = useState(false);
   const [status, setStatus] = useState("");
   const [error, setError] = useState("");

   useEffect(() => {
      if (!studentsRaw.length) dispatch(fetchStudents());
   }, [dispatch, studentsRaw.length]);

   const students = useMemo(
      () => (studentsRaw || []).filter((u) => u?.id && isStudentUser(u)),
      [studentsRaw],
   );

   useEffect(() => {
      if (!userId && students.length) setUserId(String(students[0].id));
   }, [students, userId]);

   const createDocumentReady = useCallback(async () => {
      setError("");
      setStatus("");

      const id = Number(userId);
      if (!Number.isInteger(id) || id <= 0) {
         setError("Selectează un elev valid.");
         return;
      }
      if (!message.trim()) {
         setError("Introdu un mesaj.");
         return;
      }

      const times = Math.max(1, Math.min(20, Number(count) || 1));
      setBusy(true);

      let ok = 0;
      let fail = 0;
      for (let i = 0; i < times; i += 1) {
         try {
            await notifyDocumentReady({ userId: id, message: message.trim() });
            ok += 1;
         } catch (e) {
            fail += 1;
            setError(e?.message || "Eroare la crearea notificării.");
         }
      }

      setBusy(false);
      setStatus(
         `Creat în DB: ${ok} notificare(i)${fail ? `, eșuate: ${fail}` : ""}.`,
      );
   }, [userId, message, count]);

   const runTestTomorrow = useCallback(async () => {
      setError("");
      setStatus("");
      setBusy(true);
      try {
         await testNotificationsTomorrow();
         setStatus(
            "Reamintirile pentru rezervările de mâine au fost trimise (test-tomorrow).",
         );
      } catch (e) {
         setError(e?.message || "Nu s-au putut trimite reamintirile.");
      } finally {
         setBusy(false);
      }
   }, []);

   return (
      <div className="adminOther__seeder">
         <div className="adminOther__seederHead">
            <div>
               <p className="adminOther__linkMeta">Testare</p>
               <h3 className="adminOther__seederTitle">
                  Notificări reale (DB)
               </h3>
               <p className="adminOther__seederHint">
                  Creează notificări reale în baza de date prin endpoint-urile
                  expuse: <code>document-ready</code> (per elev) și{" "}
                  <code>test-tomorrow</code> (reamintiri). Celelalte tipuri
                  (video după lecție, reamintiri de la lecția 12, rezultat
                  examen) sunt generate automat de backend.
               </p>
            </div>
         </div>

         <div className="adminOther__seederGrid">
            <label className="adminOther__seederField">
               <span>Elev</span>
               <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={busy || studentsLoading}
               >
                  {students.length === 0 ? (
                     <option value="">— fără elevi —</option>
                  ) : null}
                  {students.map((u) => (
                     <option key={u.id} value={String(u.id)}>
                        {studentLabel(u)}
                     </option>
                  ))}
               </select>
            </label>

            <label className="adminOther__seederField">
               <span>Câte notificări</span>
               <input
                  type="number"
                  min={1}
                  max={20}
                  value={count}
                  onChange={(e) => setCount(Number(e.target.value) || 1)}
                  disabled={busy}
               />
            </label>
         </div>

         <label
            className="adminOther__seederField"
            style={{ marginTop: 8, display: "block" }}
         >
            <span>Mesaj</span>
            <textarea
               rows={2}
               value={message}
               onChange={(e) => setMessage(e.target.value)}
               disabled={busy}
               style={{ width: "100%", resize: "vertical" }}
            />
         </label>

         <div
            style={{
               display: "flex",
               flexWrap: "wrap",
               gap: 6,
               marginTop: 6,
            }}
         >
            {PRESET_MESSAGES.map((preset) => (
               <button
                  key={preset}
                  type="button"
                  className="adminOther__seederBtn"
                  onClick={() => setMessage(preset)}
                  disabled={busy}
                  title={preset}
               >
                  {preset.slice(0, 24)}…
               </button>
            ))}
         </div>

         <div className="adminOther__seederFooter">
            <div className="adminOther__seederStatus">
               {status || "Pregătit."}
               {error ? (
                  <div className="adminOther__seederError">{error}</div>
               ) : null}
            </div>

            <div className="adminOther__seederActions">
               <button
                  type="button"
                  className="adminOther__seederBtn adminOther__seederBtn--primary"
                  onClick={createDocumentReady}
                  disabled={busy || !students.length}
               >
                  Creează notificare
               </button>
               <button
                  type="button"
                  className="adminOther__seederBtn"
                  onClick={runTestTomorrow}
                  disabled={busy}
               >
                  Reamintiri mâine
               </button>
            </div>
         </div>
      </div>
   );
}
